import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowRightLine } from 'react-icons/ri';
import { PiLineVerticalBold } from 'react-icons/pi';
import restaurantImg from '../../../assets/img/home.png';
import './Home.css';
import Button from '../../UI/Button/Button';
import LanguageContext from '../../../context/LanguageContext.js';
import { usePublicSettings } from '../../../context/PublicSettingsContext.js';
import { useStorefront } from '../../../context/StoreContext.js';
import { useStorePath } from '../../../lib/storefront/storePath.js';
import { fetchHeroItems } from '../../../api/heroApi.js';
import { heroItemsPlaceholder } from '../../../data/heroItemsPlaceholder.js';
import { pickLocalized } from '../../../utils/localizedContent.js';
import { resolveMediaUrl } from '../../../utils/mediaUrl.js';
import SocialLinks from '../../common/SocialLinks/SocialLinks.jsx';

/** The store's hero image, or the bundled one when the item has none (or a stock UI asset). */
function heroImageFor(item) {
    const fromApi = resolveMediaUrl(item?.imageUrl);
    if (fromApi && !fromApi.includes('/website-ui/')) return fromApi;
    return restaurantImg;
}

const Home = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const { publicSettings } = usePublicSettings();
    const { store, ready } = useStorefront();
    const storePath = useStorePath();
    const currentLanguage = i18n.language;
    const languageKey = currentLanguage.split('-')[0];

    const [heroItems, setHeroItems] = useState([]);
    const [heroLoading, setHeroLoading] = useState(true);
    const [activeHeroIndex, setActiveHeroIndex] = useState(0);
    // The skeleton stays until the first slide's image has actually loaded (not just its URL).
    const [imageReady, setImageReady] = useState(false);

    useEffect(() => {
        if (!ready || !store?.id) return undefined;

        let cancelled = false;

        (async () => {
            try {
                setHeroLoading(true);
                const items = await fetchHeroItems(store.id);
                if (!cancelled) {
                    const fromApi = Array.isArray(items) ? items : [];
                    setHeroItems(fromApi.length > 0 ? fromApi : heroItemsPlaceholder);
                    setActiveHeroIndex(0);
                }
            } catch {
                if (!cancelled) {
                    setHeroItems(heroItemsPlaceholder);
                    setActiveHeroIndex(0);
                }
            } finally {
                if (!cancelled) setHeroLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [ready, store?.id]);

    const activeHero = heroItems[activeHeroIndex] || heroItems[0] || null;

    const heroTitle = useMemo(() => {
        if (activeHero) {
            return pickLocalized(activeHero.title, languageKey, t('home.title'));
        }
        return t('home.title');
    }, [activeHero, languageKey, t]);

    const heroDescription = useMemo(() => {
        if (activeHero) {
            return pickLocalized(activeHero.description, languageKey, t('home.description'));
        }
        return t('home.description');
    }, [activeHero, languageKey, t]);

    const heroImage = useMemo(() => heroImageFor(activeHero), [activeHero]);

    // Preload every slide so later slides swap in instantly instead of popping in.
    useEffect(() => {
        heroItems.forEach((item) => {
            new Image().src = heroImageFor(item);
        });
    }, [heroItems]);

    useEffect(() => {
        if (heroItems.length <= 1) return undefined;

        const timer = window.setInterval(() => {
            setActiveHeroIndex((index) => (index + 1) % heroItems.length);
        }, 3500);

        return () => window.clearInterval(timer);
    }, [heroItems.length]);

    const socialLinks = publicSettings?.socialLinks;

    return (
        <section className="home home-hero" id="hero">
            <div className="container hv-shell">
                <div className="hv-layout" dir={dir()}>
                    <div className="hv-copy">
                        <span className="hv-badge">{t('home.heroKicker')}</span>
                        {heroLoading ? (
                            <div className="hv-skeleton-copy" role="status" aria-busy="true" aria-label={t('home.heroLoading', { defaultValue: 'Loading…' })}>
                                <span className="hv-skeleton hv-skeleton--headline" />
                                <span className="hv-skeleton hv-skeleton--headline hv-skeleton--short" />
                                <span className="hv-accent-bar" aria-hidden="true" />
                                <span className="hv-skeleton hv-skeleton--line" />
                                <span className="hv-skeleton hv-skeleton--line hv-skeleton--short" />
                            </div>
                        ) : (
                            <>
                                <h1 className="hv-headline">{heroTitle}</h1>
                                <span className="hv-accent-bar" aria-hidden="true" />
                                <p className="hv-sub">{heroDescription}</p>
                            </>
                        )}

                        <div className="hv-ctas">
                            <Button
                                to={storePath(`/${currentLanguage}/menu`)}
                                flex
                                className="hv-btn hv-btn--primary"
                            >
                                {t('home.exploreMenu')}
                            </Button>
                            <Button
                                to={storePath(`/${currentLanguage}/reservation`)}
                                variant="link"
                                flex
                                className="hv-btn hv-btn--link"
                            >
                                {t('home.bookTable')}
                                <RiArrowRightLine className="hv-btn-arrow" aria-hidden="true" />
                            </Button>
                        </div>

                        {heroItems.length > 1 ? (
                            <div className="hv-hero-dots" role="tablist" aria-label={t('home.heroSlides')}>
                                {heroItems.map((item, index) => (
                                    <button
                                        key={item._id || item.id || index}
                                        type="button"
                                        role="tab"
                                        aria-selected={index === activeHeroIndex}
                                        className={`hv-hero-dot${index === activeHeroIndex ? ' hv-hero-dot--active' : ''}`}
                                        onClick={() => setActiveHeroIndex(index)}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <div className="hv-visual">
                        <div className="hv-visual-orbit" aria-hidden="true">
                            <span className="hv-visual-dot" />
                            <span className="hv-visual-dot hv-visual-dot--b" />
                            <span className="hv-visual-dot hv-visual-dot--c" />
                        </div>
                        {/* Fixed aspect-ratio frame: every slide (wide or tall) and the skeleton
                            fill exactly the same box, so nothing shifts between them. */}
                        <div className="hv-frame">
                            {!heroLoading && (
                                <img
                                    src={heroImage}
                                    alt={heroTitle || t('home.heroImageAlt')}
                                    className={`hv-img${imageReady ? ' hv-img--ready' : ''}`}
                                    decoding="async"
                                    onLoad={() => setImageReady(true)}
                                    onError={() => setImageReady(true)}
                                />
                            )}
                            {(heroLoading || !imageReady) && (
                                <div className="hv-skeleton hv-frame-skeleton" aria-hidden="true" />
                            )}
                        </div>
                    </div>
                </div>

                <div className="hv-social" dir={dir()}>
                    <span className="hv-social-follow">{t('home.followUs')}</span>
                    <PiLineVerticalBold className="hv-social-divider" aria-hidden="true" />
                    <SocialLinks
                        socialLinks={socialLinks}
                        className="hv-social-links"
                        linkClassName="hv-social-link"
                    />
                </div>
            </div>
        </section>
    );
};

export default Home;
